import { Home, PanelLeftOpen, PanelRightOpen, Plus, Settings, User, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';

/**
 * Props for the AppSidebar component
 */
export interface AppSidebarProps {
  /** Whether the WorkspaceSidebar (full sidebar) is expanded */
  workspaceSidebarOpen: boolean;
  /** Toggle the workspace sidebar open/closed */
  onWorkspaceSidebarToggle: () => void;
  /** Create a new workspace */
  onCreateWorkspace: () => void;
  /** Navigate to home page */
  onHomeClick?: () => void;
  /** Navigate to settings page */
  onSettingsClick: () => void;
  /** Sign out handler */
  onSignOut?: () => void;
  /** User email to display in account menu */
  userEmail?: string | null;
}

/**
 * Narrow icon-only sidebar for app-level actions.
 * Sits on the left edge of the application.
 *
 * @example
 * ```tsx
 * <AppSidebar
 *   workspaceSidebarOpen={isSidebarOpen}
 *   onWorkspaceSidebarToggle={() => setIsSidebarOpen(!isSidebarOpen)}
 *   onCreateWorkspace={handleCreateWorkspace}
 *   onSettingsClick={() => navigate('/settings')}
 * />
 * ```
 */
export function AppSidebar({
  workspaceSidebarOpen,
  onWorkspaceSidebarToggle,
  onCreateWorkspace,
  onHomeClick,
  onSettingsClick,
  onSignOut,
  userEmail,
}: AppSidebarProps) {
  const iconBtnClass =
    'w-8 h-8 flex items-center justify-center rounded-lg ' +
    'text-muted-foreground hover:text-foreground hover:bg-background transition-colors';

  return (
    <div className="w-10 flex-shrink-0 bg-background flex flex-col items-center py-2 z-10">
      {/* Top section: main actions */}
      <div className="flex flex-col items-center gap-1">
        <button
          onClick={onWorkspaceSidebarToggle}
          className={iconBtnClass}
          title={workspaceSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {workspaceSidebarOpen ? <PanelRightOpen size={16} /> : <PanelLeftOpen size={16} />}
        </button>

        <button onClick={onCreateWorkspace} className={iconBtnClass} title="Create new workspace">
          <Plus size={16} />
        </button>

        {onHomeClick && (
          <button onClick={onHomeClick} className={iconBtnClass} title="Home">
            <Home size={16} />
          </button>
        )}
      </div>

      <div className="flex-1" />

      {/* Bottom section: user account */}
      <div className="flex flex-col items-center pb-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-full
                ring-2 ring-border text-muted-foreground
                hover:text-foreground hover:ring-foreground/40 transition-all"
              title="Account"
            >
              <User size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end">
            {userEmail && (
              <>
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground truncate max-w-48">
                  {userEmail}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={onSettingsClick}>
              <Settings size={14} />
              Settings
            </DropdownMenuItem>
            {onSignOut && (
              <DropdownMenuItem onClick={onSignOut}>
                <LogOut size={14} />
                Log out
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
